import { daylight, momentAt, MOMENTS, PLACE, sunAt } from "./daylight.js";
import { forecast } from "./forecast.js";
import { report } from "./report.js";

const root = document.documentElement;
const stage = document.querySelector(".stage");
const loading = stage.querySelector(".note.slow > span");
const loadingPercent = loading.querySelector(".percent");
const hint = stage.querySelector(".hint span");
const brand = document.querySelector(".brand");
const panel = document.querySelector(".panel");
const pages = [...panel.querySelectorAll("article[data-stop]")];
const tabs = panel.querySelector(".stops");
const links = [...tabs.querySelectorAll("a")];
const themeColour = document.querySelector('meta[name="theme-color"]');
const knobs = document.querySelector(".knobs");
const knobToggle = knobs.querySelector(".toggle");
const knobChoices = knobs.querySelector(".choices");
const foldButton = panel.querySelector(".fold");
const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
const wide = matchMedia("(min-width: 800px)");
const params = new URLSearchParams(location.search);

const STOP_FOR_HASH = { "#inicio": "inicio", "#produtos": "produtos", "#casinha": "casinha", "#como-chegar": "chegar" };
const HASH_FOR_STOP = Object.fromEntries(Object.entries(STOP_FOR_HASH).map(([hash, stop]) => [stop, hash]));
// Zooming past the end of a stop's reach: in from the start to the casinha and on inside it, out the same way back.
const ZOOM_TO = { in: { inicio: "casinha", casinha: "produtos" }, out: { produtos: "casinha", casinha: "inicio", chegar: "inicio" } };
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_NAMES = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const WEATHERS = {
  sol: { rain: 0, frost: 0, cloud: 0 },
  nuvens: { rain: 0, frost: 0, cloud: 1 },
  chuva: { rain: 1, frost: 0, cloud: 0 },
  geada: { rain: 0, frost: 1, cloud: 0 },
};
// Each knob follows the shop, showing the moment and the weather there, until a setting is picked
// on it. ?ceu=noite&tempo=chuva opens on picked settings.
const SETTINGS = { ceu: Object.keys(MOMENTS), tempo: Object.keys(WEATHERS) };
const SWEEP_MS = 1800;
const FORECAST_MS = 15 * 60000;

let viewer = null;
let current = null;
// ?dev's panel (dev.js) pins the weather or the shop open or shut over the knobs and the clock while set.
const pinned = { weather: null, open: null };

const setting = {};
const following = {};
for (const [name, values] of Object.entries(SETTINGS)) {
  following[name] = !values.includes(params.get(name));
  setting[name] = following[name] ? values[0] : params.get(name);
}
knobs.addEventListener("change", ({ target }) => {
  if (target.name === "ceu" || target.value === "ceu") replay.from = null;
  if (target.name === "agora") {
    following[target.value] = target.checked;
  } else {
    following[target.name] = false;
    setting[target.name] = target.value;
  }
  tick({ sweep: true });
});

// The brand's old logo, in the header and the miniature: any ?logo in the address shows the knob between
// it and the logo, and ?logo=antigo opens on it. Without one the knob goes, and nothing of it is fetched.
const LOGOS = {
  novo: { src: "img/logo.svg", width: 769, height: 395 },
  antigo: { src: "img/old-logo.svg", width: 1120, height: 380 },
};
const logoKnob = knobs.querySelector(".logo");
let logo = params.get("logo") === "antigo" ? "antigo" : "novo";
function showLogo(name) {
  logo = name;
  knobs.elements.logo.value = name;
  Object.assign(brand.querySelector("h1 img"), LOGOS[name]);
  root.dataset.logo = name;
  viewer?.oldLogo(name === "antigo");
}
if (params.has("logo")) {
  logoKnob.hidden = false;
  logoKnob.addEventListener("change", (event) => {
    event.stopPropagation();
    showLogo(event.target.value);
    tick();
  });
  showLogo(logo);
} else {
  logoKnob.remove();
}

// Two paints for the road sign, to try beside the trailer's blue it has: its posts and board white as in the
// photo, and that with its logo reversed, white on blue as on the sign in the photo. Any ?logo or ?placa shows
// the knob, and ?placa=branca or ?placa=azul opens on one.
const SIGN_PAINTS = ["estrutura", "branca", "azul"];
const signKnob = knobs.querySelector(".placa");
const signPaint = SIGN_PAINTS.includes(params.get("placa")) ? params.get("placa") : "estrutura";
if (params.has("logo") || params.has("placa")) {
  signKnob.hidden = false;
  knobs.elements.placa.value = signPaint;
  signKnob.addEventListener("change", (event) => {
    event.stopPropagation();
    placeThumbs(true);
    viewer?.signPaint(event.target.value);
  });
} else {
  signKnob.remove();
}

// The model's textures as it ships them, lighter, or as they were before, to compare: any ?textura shows the knob,
// and ?textura=original opens on the originals. Without one nothing of them is fetched.
const TEXTURES = ["leves", "originais"];
const textureKnob = knobs.querySelector(".textura");
const texture = TEXTURES.includes(params.get("textura")) ? params.get("textura") : "leves";
if (params.has("textura")) {
  textureKnob.hidden = false;
  knobs.elements.textura.value = texture;
  textureKnob.addEventListener("change", (event) => {
    event.stopPropagation();
    placeThumbs(true);
    viewer?.textures(event.target.value === "originais");
  });
} else {
  textureKnob.remove();
}

// At most 60 frames a second, as the page draws, or 20, to see how the scene moves at that rate: any ?fps shows the
// knob, and ?fps=20 opens on 20. Any other rate, ?fps=8, adds itself to the knob and opens on it.
const rateKnob = knobs.querySelector(".fps");
const rate = Number(params.get("fps")) > 0 ? String(Math.min(60, Number(params.get("fps")))) : "60";
if (params.has("fps")) {
  rateKnob.hidden = false;
  if (!knobs.querySelector(`[name="fps"][value="${rate}"]`)) {
    const label = rateKnob.querySelector('[value="20"]').closest("label").cloneNode(true);
    label.querySelector("input").value = rate;
    label.querySelector("span").textContent = `${rate} fps`;
    rateKnob.querySelector(".options").append(label);
  }
  knobs.elements.fps.value = rate;
  rateKnob.addEventListener("change", (event) => {
    event.stopPropagation();
    placeThumbs(true);
    viewer?.frameRate(Number(event.target.value));
  });
} else {
  rateKnob.remove();
}

const reading = knobs.querySelector(".agora .reading");
const dusk = knobs.querySelector('[value="por-do-sol"] ~ span');

// On a phone the knobs fold into one pill, which shows the icons of the hour and weather, and the
// temperature while the weather is the shop's.
function showKnobs(date, sun, sliding) {
  for (const name of Object.keys(SETTINGS)) {
    if (!bar || name !== "ceu") knobs.elements[name].value = setting[name];
    knobs.querySelector(`[name="agora"][value="${name}"]`).checked = following[name];
  }
  if (bar) showBar(date, sun, sliding);
  else dusk.textContent = following.ceu && setting.ceu === "por-do-sol" && sun.hourAngle < 0 ? "Alvorada" : "Pôr do sol";
  reading.textContent = live.temperature === null ? "" : `${Math.round(live.temperature)}°`;
  const picked = [...knobs.querySelectorAll("[type=radio]:checked")].map((input) => input.closest("label"));
  if (bar) picked.unshift(thumb);
  const temperature = following.tempo && reading.textContent ? [reading.cloneNode(true)] : [];
  knobToggle.replaceChildren(...picked.map((label) => label.querySelector("svg").cloneNode(true)), ...temperature);
  const words = [...picked.map((label) => label.textContent), ...temperature.map((node) => node.textContent)];
  knobToggle.setAttribute("aria-label", `Hora e tempo: ${words.join(", ")}`);
  placeThumbs(true);
}

// A thumb slides when the pick changes, and jumps when the labels are laid out again: when the
// fonts arrive, the popover opens, or the screen turns wide.
function placeThumb(row, picked, sliding) {
  row.classList.toggle("sliding", sliding);
  row.style.setProperty("--thumb-left", `${picked.offsetLeft}px`);
  row.style.setProperty("--thumb-top", `${picked.offsetTop}px`);
  row.style.setProperty("--thumb-width", `${picked.offsetWidth}px`);
  row.style.setProperty("--thumb-height", `${picked.offsetHeight}px`);
}
function placeThumbs(sliding) {
  for (const options of knobs.querySelectorAll(".options")) {
    placeThumb(options, options.querySelector(":checked").closest("label"), sliding);
  }
}
const relayout = new ResizeObserver(() => placeThumbs(false));
for (const label of knobs.querySelectorAll(".options label")) relayout.observe(label);

const tabThumb = document.createElement("div");
const litTabs = document.createElement("div");
tabThumb.className = "thumb";
tabThumb.inert = true;
tabThumb.ariaHidden = "true";
litTabs.className = "stops";
litTabs.append(...links.map((link) => link.cloneNode(true)));
tabThumb.append(litTabs);
tabs.append(tabThumb);
new ResizeObserver(() => {
  placeThumb(tabs, tabs.querySelector("[aria-current]"), false);
  root.style.setProperty("--tabs-height", `${tabs.offsetHeight}px`);
}).observe(tabs);
// On a phone the knobs open over the panel's page, which takes their height (site.css).
new ResizeObserver(() => root.style.setProperty("--knobs-height", `${knobChoices.offsetHeight}px`)).observe(knobChoices);

// Safari before 17 has no popovers, and the rows stood open over the page for good: there they stay shut, and the pill
// only shows the hour and the weather.
const popovers = "togglePopover" in HTMLElement.prototype;
if (!popovers) {
  knobChoices.hidden = true;
  knobToggle.disabled = true;
}

function closeKnobs() {
  if (popovers && knobChoices.matches(":popover-open")) knobChoices.hidePopover();
}

function setFolded(folded) {
  panel.classList.toggle("folded", folded);
  foldButton.setAttribute("aria-expanded", String(!folded));
  panel.querySelector(".pages").scrollTop = 0;
}
// The grabber also swipes: up opens the panel, down folds it. A swipe can still end in a click, which
// must not toggle it straight back; only some do, so a click is ignored only just after one.
let grabbedAt = null;
let swipedAt = -Infinity;
foldButton.addEventListener("pointerdown", (event) => {
  grabbedAt = event.clientY;
  foldButton.setPointerCapture(event.pointerId);
});
foldButton.addEventListener("pointerup", (event) => {
  const dy = event.clientY - (grabbedAt ?? event.clientY);
  grabbedAt = null;
  if (Math.abs(dy) < 16) return;
  swipedAt = performance.now();
  setFolded(dy > 0);
});
foldButton.addEventListener("click", () => {
  if (performance.now() - swipedAt < 500) return;
  setFolded(!panel.classList.contains("folded"));
});
panel.querySelector(".pages").addEventListener("click", (event) => {
  if (!wide.matches && event.target.closest("h2")) setFolded(!panel.classList.contains("folded"));
});

// Hours by weekday (0 is Sunday) in minutes after midnight, from the page's structured data.
const hours = new Map();
const business = JSON.parse(document.querySelector('script[type="application/ld+json"]').textContent);
for (const spec of business.openingHoursSpecification ?? []) {
  const [opens, closes] = [spec.opens, spec.closes].map((time) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  });
  for (const day of [spec.dayOfWeek].flat()) {
    hours.set(WEEKDAYS.indexOf(day.replace(/^https?:\/\/schema\.org\//, "")), { opens, closes });
  }
}

const shopClock = new Intl.DateTimeFormat("en-US", {
  timeZone: PLACE.timeZone, weekday: "long", hour: "numeric", minute: "numeric", hourCycle: "h23",
});
function atTheShop(date) {
  const parts = Object.fromEntries(shopClock.formatToParts(date).map(({ type, value }) => [type, value]));
  return { weekday: WEEKDAYS.indexOf(parts.weekday), minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}

// ?hora=19:30 shows the page as it looks at that time today at the shop, and
// ?hora=2026-06-21T17:50 at that moment on that day, for a winter dusk with the shop still open.
function now() {
  const wanted = params.get("hora") ?? "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(wanted)) return new Date(`${wanted}:00-03:00`);
  const date = new Date();
  const time = /^(\d{1,2}):(\d{2})$/.exec(wanted);
  if (!time) return date;
  return todayAt(Number(time[1]) * 60 + Number(time[2]), date);
}

// A page opened in the shop's hours first plays its morning (JP): its clock stands MORNING_LEAD minutes before opening
// while Vitor backs the trailer in and walks in, then runs on to now, and the first customer comes. ?direto opens on
// now. `from` is the morning's moment, and `along` how far the clock has run on from it; the panel keeps the real hours.
// A morning the scene buttons play out of hours runs on `to` a picked hour instead. `cut` counts the scene buttons'
// cuts, so a run-on a cut overtook stops where it is.
const MORNING_LEAD = 15;
const replay = { from: null, along: 0, to: null, cut: 0 };
function shownAt(date) {
  if (replay.from === null) return date;
  return new Date(replay.from + ((replay.to ?? date.getTime()) - replay.from) * replay.along);
}

// Runs the clock on to now: a second and a half, and half a second more for each hour run.
function runOn(cut) {
  if (replay.from === null) return viewer?.morningOver();
  const start = performance.now();
  const duration = 1500 + ((replay.to ?? now().getTime()) - replay.from) / 3600000 * 500;
  requestAnimationFrame(function step(time) {
    if (replay.cut !== cut) return;
    const t = replay.from === null ? 1 : Math.min(1, (time - start) / duration);
    replay.along = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
    if (t === 1 && replay.from !== null) {
      if (replay.to !== null) pickHour(atTheShop(new Date(replay.to)).minutes);
      replay.from = null;
    }
    tick();
    if (t < 1) requestAnimationFrame(step);
    else viewer?.morningOver();
  });
}

// Stops the hour knob following the clock, at `minutes` after midnight today.
function pickHour(minutes) {
  following.ceu = false;
  setting.ceu = bar ? minutes : momentAt(sunAt(todayAt(minutes, now())));
}

// The moment on date's day when the shop's clock reads `minutes` after midnight.
function todayAt(minutes, date) {
  return new Date(date.getTime() + (minutes - atTheShop(date).minutes) * 60000);
}

function clockTime(minutes) {
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

function status(date) {
  const { weekday, minutes } = atTheShop(date);
  const today = hours.get(weekday);
  if (today && minutes >= today.opens && minutes < today.closes) {
    return { open: true, text: `Hoje até as ${clockTime(today.closes)}` };
  }
  if (today && minutes < today.opens) return { open: false, text: `Abre hoje às ${clockTime(today.opens)}` };
  for (let ahead = 1; ahead <= 7; ahead++) {
    const day = hours.get((weekday + ahead) % 7);
    if (!day) continue;
    const when = ahead === 1 ? "amanhã" : DAY_NAMES[(weekday + ahead) % 7];
    return { open: false, text: `Abre ${when} às ${clockTime(day.opens)}` };
  }
  return { open: false, text: "" };
}

// The hour knob is a bar dragged through today; ?botoes swaps it back for the three moments it replaced.
// ?ceu puts it where the sun stands at that moment. It runs through the whole day, 0h to 23h59, and
// paints it from day blue to night navy. The hour picked is minutes after midnight at the shop; the
// input's value is a place along the bar, where a minute takes the room ROOM gives it.
const bar = !params.has("botoes");
const track = knobs.querySelector(".track");
const thumb = track.querySelector(".thumb");
const hour = track.querySelector("input");
const places = Number(hour.max);
const [first, last] = [0, 24 * 60 - 1];
// The shop's opening hours get twice the room of daylight; dawn and dusk, where the sky changes, the
// most, even while the shop is shut; the night, all alike, little.
const ROOM = { open: 2, night: 0.25, twilight: 3, day: 1 };
// The sun's heights between which the sky changes: the ends of SKIES in daylight.js.
const [SKY_DAY, SKY_NIGHT] = [20, -12];
const moments = {};
if (bar) {
  const options = track.previousElementSibling;
  for (const label of options.querySelectorAll("label")) {
    moments[label.querySelector("input").value] = { icon: label.querySelector("svg"), word: label.textContent };
  }
  options.remove();
  if (!following.ceu) setting.ceu = minuteOf(setting.ceu, now());
  hour.addEventListener("input", () => {
    // A jump, as a click along the bar makes, sweeps the sky there; a drag carries it along.
    const minutes = minutesAt(hour.valueAsNumber);
    const leap = Math.abs(minutes - setting.ceu) > 30;
    following.ceu = false;
    replay.from = null;
    setting.ceu = minutes;
    tick({ sweep: leap });
  });
  hour.addEventListener("change", (event) => event.stopPropagation());
} else {
  track.previousElementSibling.hidden = false;
  track.remove();
}

function minuteOf(moment, date) {
  if (moment === "dia") return 600;
  let minutes = 720;
  while (minutes < last && sunAt(todayAt(minutes, date)).elevation > MOMENTS[moment].elevation) minutes += 5;
  return minutes;
}

// The sky and the shop at the hour picked. A moment shows the shop as a visitor stopping by then would
// find it, open by day and at dusk and closed at night; the bar, as its hours have it at that time today.
// The panel keeps telling the real hours.
function atPickedHour(date) {
  if (!bar) return { sun: MOMENTS[setting.ceu], open: setting.ceu !== "noite" };
  const then = todayAt(setting.ceu, date);
  return { sun: sunAt(then), open: status(then).open };
}

// Where each minute of today falls along the bar, 0 to `places`, a length the input's step divides.
let offsets = [];
function layBar(date) {
  const room = [0];
  const today = hours.get(atTheShop(date).weekday);
  for (let minutes = first; minutes < last; minutes++) {
    const { elevation } = sunAt(todayAt(minutes + 0.5, date));
    const open = today && minutes >= today.opens && minutes < today.closes;
    room.push(room.at(-1) + (open ? ROOM.open
      : elevation >= SKY_DAY ? ROOM.day : elevation > SKY_NIGHT ? ROOM.twilight : ROOM.night));
  }
  offsets = room.map((offset) => offset / room.at(-1) * places);
  // The blue darkens evenly through each twilight, by the sun's height. The sky overhead would not: it
  // keeps nearly all of its change for 7 degrees up to 5 below, a hard edge a third of the twilight wide.
  const [day, night] = [SKY_DAY, SKY_NIGHT].map((elevation) => daylight({ elevation, azimuth: 0 }).sky.zenith);
  const stops = [];
  for (let i = 0; i <= 64; i++) {
    const { elevation } = sunAt(todayAt(minutesAt(i / 64 * places), date));
    const dark = Math.min(Math.max((SKY_DAY - elevation) / (SKY_DAY - SKY_NIGHT), 0), 1);
    const at = `calc(var(--thumb-width) / 2 + ${(i / 64).toFixed(4)} * (100% - var(--thumb-width)))`;
    stops.push(`color-mix(in oklab, ${day}, ${night} ${(dark * 100).toFixed(1)}%) ${at}`);
  }
  track.style.setProperty("--hours", `linear-gradient(in oklab 90deg, ${stops.join(", ")})`);
}

function placeOf(minutes) {
  const at = Math.min(Math.max(minutes, first), last) - first;
  const i = Math.min(Math.floor(at), offsets.length - 2);
  return offsets[i] + (offsets[i + 1] - offsets[i]) * (at - i);
}

function minutesAt(place) {
  let i = 0;
  while (i < offsets.length - 2 && offsets[i + 1] < place) i++;
  return first + i + (place - offsets[i]) / (offsets[i + 1] - offsets[i]);
}

// The thumb carries the moment's icon and the time, and slides only when the hour jumps.
let paintedDay = null;
function showBar(date, sun, sliding) {
  const day = date.toLocaleDateString("en-CA", { timeZone: PLACE.timeZone });
  if (day !== paintedDay) {
    paintedDay = day;
    layBar(date);
  }
  const moment = momentAt(sun);
  const word = moment === "por-do-sol" && sun.hourAngle < 0 ? "Alvorada" : moments[moment].word;
  const time = clockTime(Math.round(setting.ceu));
  thumb.replaceChildren(moments[moment].icon.cloneNode(true), time);
  const place = placeOf(setting.ceu);
  hour.value = place;
  hour.setAttribute("aria-valuetext", `${time}, ${word.toLowerCase()}`);
  track.classList.toggle("sliding", sliding);
  track.style.setProperty("--along", (place / places).toFixed(4));
}

// The sky shown, as the sun's position and the weather. A turn of a knob sweeps it to the new
// setting, so the day passes through dusk on its way to night.
const scene = { elevation: 0, hourAngle: 0, azimuth: 0, rain: 0, frost: 0, cloud: 0 };
let sweep = null;

function paint() {
  const light = daylight(scene, scene);
  root.style.setProperty("--zenith", light.sky.zenith);
  root.style.setProperty("--horizon", light.sky.horizon);
  root.style.setProperty("--night", light.night.toFixed(3));
  root.style.setProperty("--overcast", light.weather.overcast.toFixed(3));
  themeColour.content = light.sky.zenith;
  viewer?.sky(light);
}

function aim(goal, sweeping) {
  if (!sweeping || still || !viewer) {
    sweep = null;
    Object.assign(scene, goal);
    paint();
    return;
  }
  const running = sweep;
  sweep = { from: { ...scene }, goal, start: performance.now() };
  if (!running) requestAnimationFrame(sweepFrame);
}

function sweepFrame(now) {
  if (!sweep) return;
  const t = Math.min(1, (now - sweep.start) / SWEEP_MS);
  const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
  for (const key of Object.keys(scene)) scene[key] = sweep.from[key] + (sweep.goal[key] - sweep.from[key]) * eased;
  paint();
  if (t < 1) requestAnimationFrame(sweepFrame);
  else sweep = null;
}

// The weather at the shop, clear until the forecast answers and kept as it was when a refresh fails.
let live = { tempo: "sol", temperature: null, weather: WEATHERS.sol };
let asked = -Infinity;

function askForecast(date) {
  if (performance.now() - asked < FORECAST_MS) return;
  asked = performance.now();
  forecast(date)
    .then(({ tempo, temperature, ...weather }) => {
      live = { tempo, temperature, weather };
      tick({ sweep: true });
    })
    .catch((error) => console.warn("No forecast:", error));
}

let shown = null;
function tick({ sweep: sweeping = false } = {}) {
  const date = now();
  askForecast(date);
  const clock = shownAt(date);
  const sun = sunAt(clock);
  if (following.ceu) setting.ceu = bar ? atTheShop(clock).minutes : momentAt(sun);
  if (following.tempo) setting.tempo = live.tempo;
  const picked = atPickedHour(date);
  const shownSun = following.ceu ? sun : picked.sun;
  showKnobs(date, shownSun, sweeping);
  const goal = { ...shownSun, ...(pinned.weather ?? (following.tempo ? live.weather : WEATHERS[setting.tempo])) };
  if (sweep && !sweeping) sweep.goal = goal;
  else aim(goal, sweeping);

  const { open, text } = status(date);
  const changed = shown !== null && shown !== open;
  for (const line of document.querySelectorAll(".status")) {
    line.classList.toggle("closed", !open);
    line.querySelector(".plate").textContent = open ? "Aberto" : "Fechado";
    line.querySelector(".when").textContent = text;
    if (changed || shown === null) swing(line.querySelector(".plate"));
  }
  shown = open;
  const sceneOpen = pinned.open ?? (following.ceu ? status(clock).open : picked.open);
  helping.closed = !sceneOpen;
  placeHelpers();
  viewer?.open(sceneOpen);
  nameScenes();
}

function swing(plate) {
  plate.classList.remove("swing");
  void plate.offsetWidth;
  plate.classList.add("swing");
}

// The part of the stage the brand and the panel leave uncovered: above the panel on a phone,
// left of it on a wide screen.
function insets() {
  const { width, height } = stage.getBoundingClientRect();
  const box = panel.getBoundingClientRect();
  if (wide.matches) return { top: 24, right: width - box.left + 12, bottom: 24, left: 24 };
  const top = Math.max(brand.getBoundingClientRect().bottom, knobs.getBoundingClientRect().bottom);
  return { top: top + 6, right: 0, bottom: height - box.top + 6, left: 0 };
}

function frame(options) {
  const values = insets();
  for (const [side, value] of Object.entries(values)) stage.style.setProperty(`--inset-${side}`, `${value}px`);
  viewer?.frame(values, options);
  return values;
}

function show(stop) {
  if (stop === current) return;
  const first = current === null;
  current = stop;
  for (const page of pages) page.hidden = page.dataset.stop !== stop;
  const page = pages.find((candidate) => candidate.dataset.stop === stop);
  for (const link of links) {
    if (STOP_FOR_HASH[link.hash] === stop) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  }
  placeThumb(tabs, tabs.querySelector("[aria-current]"), !first);
  document.body.dataset.stop = stop;
  stage.setAttribute("aria-label", page.dataset.label);
  hint.textContent = page.dataset.hint;
  panel.querySelector(".pages").scrollTop = 0;
  closeKnobs();
  frame();
  placeHelpers();
  viewer?.go(stop);
}

addEventListener("hashchange", () => show(STOP_FOR_HASH[location.hash] ?? "inicio"));
// The tab already chosen flies the camera back to its view, after the visitor has wandered off.
for (const link of links) {
  link.addEventListener("click", (event) => {
    if (link.hash !== location.hash && !(link.hash === "#inicio" && !location.hash)) return;
    event.preventDefault();
    viewer?.go(STOP_FOR_HASH[link.hash]);
  });
}
const products = [...panel.querySelectorAll(".products button")];
// The pictures are WebP only, which a browser before it (iOS 13 and older) draws as an empty box: there the tiles keep
// just their names. Asked of a one-pixel WebP, as a lazy picture not yet loaded cannot tell a failure from a wait.
const webp = new Image();
webp.onerror = () => {
  for (const picture of panel.querySelectorAll(".products img")) picture.remove();
};
webp.src = "data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==";
for (const product of products) {
  product.addEventListener("click", () => {
    const picking = product.getAttribute("aria-pressed") !== "true";
    for (const other of products) other.setAttribute("aria-pressed", String(picking && other === product));
    for (const tag of stage.querySelectorAll('.tag[data-stop="produtos"]')) {
      tag.classList.toggle("picked", picking && tag.dataset.anchor === product.dataset.anchor);
    }
  });
}
new ResizeObserver(() => frame()).observe(panel);
wide.addEventListener("change", () => {
  closeKnobs();
  frame();
});

// The pills over the scene: the scene buttons, and between them the drag hint until the first drag, or after hours, in
// its place, the way to the day until it is picked (JP: one row), in the scene's middle once the trailer is towed off;
// a tow brings it back. The page's morning is not after hours, though its clock and the away trailer say so: it brings
// the day itself. With neither between them, the pause button is (JP). One leaving fades out before the rest slide to
// their new places.
const pills = Object.fromEntries(["previous", "hint", "day", "pause", "next"].map((name) => [name, stage.querySelector(`.helpers .${name}`)]));
const helping = { dragged: false, picked: false, closed: false, towed: false, leaving: false, held: false };
function placeHelpers() {
  if (helping.leaving) return;
  const towed = stage.hasAttribute("data-towed");
  if (towed && !helping.towed) helping.picked = false;
  helping.towed = towed;
  const ready = stage.classList.contains("ready");
  const scenes = ready && !still && current !== "produtos";
  const day = ready && replay.from === null && !helping.picked && (helping.closed || towed);
  const hint = ready && !helping.dragged && !day;
  const wanted = { previous: scenes, hint, day, pause: scenes && !hint && !day, next: scenes };
  if (!wanted.pause) hold(false);
  const names = Object.keys(pills);
  const going = names.filter((name) => !wanted[name] && !pills[name].hidden);
  if (going.length && !still) {
    helping.leaving = true;
    Promise.all(going.map((name) => pills[name].animate({ opacity: [1, 0] }, { duration: 250, fill: "forwards" }).finished))
      .then(() => {
        helping.leaving = false;
        slide(() => {
          for (const name of going) pills[name].hidden = true;
          for (const name of going) pills[name].getAnimations().forEach((animation) => animation.cancel());
        });
        placeHelpers();
      });
    return;
  }
  if (names.every((name) => pills[name].hidden === !wanted[name]) && pills.day.classList.contains("middle") === towed) return;
  slide(() => {
    for (const name of names) pills[name].hidden = !wanted[name];
    pills.day.classList.toggle("middle", towed);
  });
}
// Moves the pills from where they stood before `change` to where it leaves them.
function slide(change) {
  const before = Object.values(pills).map((pill) => pill.getBoundingClientRect());
  change();
  if (still) return;
  Object.values(pills).forEach((pill, k) => {
    const after = pill.getBoundingClientRect();
    const [x, y] = [before[k].left - after.left, before[k].top - after.top];
    if (before[k].width && after.width && (x || y)) {
      pill.animate({ translate: [`${x}px ${y}px`, "0 0"] }, { duration: 400, easing: "ease-in-out" });
    }
  });
}
new MutationObserver(placeHelpers).observe(stage, { attributeFilter: ["data-towed"] });
// A drag on the scene puts the hint away; a press on a button over it does not.
stage.addEventListener("pointerdown", function dragged(event) {
  if (event.target.closest("button, a")) return;
  helping.dragged = true;
  placeHelpers();
  stage.removeEventListener("pointerdown", dragged);
});
// Holds the scene where it stands, or lets it go on; a scene held where the button is gone could not be let go.
function hold(held) {
  if (helping.held === held) return;
  helping.held = held;
  viewer?.hold(held);
  pills.pause.classList.toggle("held", held);
  pills.pause.querySelector("button").setAttribute("aria-label", held ? "Continuar a cena" : "Pausar a cena");
}
pills.pause.querySelector("button").addEventListener("click", () => hold(!helping.held));
// After hours the hour knob picks the morning, which backs the trailer in should it have been towed off.
pills.day.querySelector("button").addEventListener("click", () => {
  helping.picked = true;
  following.ceu = false;
  replay.from = null;
  setting.ceu = bar ? minuteOf("dia", now()) : "dia";
  tick({ sweep: true });
});

// The scene buttons cut to the scene before or after the one playing, round the day (JP): the morning, as the tractor
// backs the trailer in; a customer Vitor takes a crate out to; one who walks in to the counter; a parent and child who
// buy and drink on the bench; the day's last customer; the shutting up and the tow. Each plays as a page opened in it
// does, at the clock's hour when that falls in it, else at one picked for it; a customer's scene calls its customer, as
// ?dev's do. Not at the counter, as the trailer the camera stands in is towed off.
const SCENES = [
  { label: "a chegada da casinha" },
  { label: "a entrega no carro", call: { walkIn: false } },
  { label: "o cliente no balcão", call: { walkIn: true, pair: false } },
  { label: "a família no banco", call: { kind: "suv", spot: 1, walkIn: true, pair: true } },
  { label: "o último cliente", evening: true },
  { label: "o fechamento e o reboque", evening: true, served: true },
];
const EVENING = SCENES.findIndex((scene) => scene.evening);
// The customer's scene last cut to: the customers who come after its own still count as it playing.
let visiting = 1;
const playing = () => (replay.from !== null ? 0 : helping.closed ? EVENING + (viewer?.lastOrder() ? 0 : 1) : visiting);
function nameScenes() {
  const k = playing();
  for (const [name, way, verb] of [["previous", -1, "Voltar para"], ["next", 1, "Avançar para"]]) {
    pills[name].querySelector("button").setAttribute("aria-label", `${verb} ${SCENES[(k + way + SCENES.length) % SCENES.length].label}`);
  }
}
function cutTo(k) {
  const date = now();
  const today = hours.get(atTheShop(date).weekday) ?? [...hours.values()][0];
  const open = status(date).open;
  replay.cut++;
  replay.from = null;
  if (k === 0) {
    following.ceu = true;
    Object.assign(replay, { from: todayAt(today.opens - MORNING_LEAD, date).getTime(), along: 0,
      to: open ? null : todayAt(minuteOf("dia", date), date).getTime() });
  } else if (!SCENES[k].evening) {
    visiting = k;
    if (open) following.ceu = true;
    else pickHour(minuteOf("dia", date));
  } else {
    // The tow waits for the lamps: the scene starts at the first minute past closing that the knob reads night.
    let evening = today.closes;
    while (evening < last && momentAt(sunAt(todayAt(evening, date))) !== "noite") evening += 5;
    if (!open && atTheShop(date).minutes >= evening) following.ceu = true;
    else pickHour(evening);
  }
  // A sweep under way would hold the lamps off for the evening's first open().
  sweep = null;
  hold(false);
  viewer.cut(SCENES[k]);
  const cut = replay.cut;
  if (k === 0) viewer.morning().then(() => runOn(cut));
  tick();
}
// The last customer drives off between the clock's ticks, which turns the evening's scene to the shutting up.
for (const type of ["pointerover", "focusin"]) pills.previous.parentElement.addEventListener(type, nameScenes);
// Held down, a scene button plays the scene fast instead (JP), `times` as fast once held `after` ms, until let go: the
// next one forward, the previous one back through what the scene did, after which it plays on from there. A press let
// go sooner still cuts to the next or previous scene.
const FAST = [{ after: 300, times: 4 }, { after: 2000, times: 8 }];
function holdToPlay(name, cut, play) {
  const pill = pills[name];
  const button = pill.querySelector("button");
  let fast = null;
  function press() {
    if (fast) return;
    fast = { on: false, timers: FAST.map(({ after, times }) => setTimeout(() => {
      fast.on = true;
      hold(false);
      play(times);
      pill.classList.add("fast");
    }, after)) };
  }
  // True when the press played the scene, not a cut.
  function letGo() {
    if (!fast) return false;
    const { on, timers } = fast;
    fast = null;
    timers.forEach(clearTimeout);
    if (on) play(0);
    pill.classList.remove("fast");
    return on;
  }
  // A press that played swallows the click it ends in; one let go off the button may end in none.
  let pressedFast = false;
  button.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    pressedFast = false;
    button.setPointerCapture(event.pointerId);
    press();
  });
  for (const type of ["pointerup", "pointercancel", "lostpointercapture"]) button.addEventListener(type, () => {
    if (letGo()) pressedFast = true;
  });
  button.addEventListener("click", () => {
    if (pressedFast) pressedFast = false;
    else cut();
  });
  // From the keyboard, Space or Enter held down does the same; the button's own click, on one or the other, is held back.
  button.addEventListener("keydown", (event) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (!event.repeat) press();
  });
  button.addEventListener("keyup", (event) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (!letGo()) cut();
  });
  // A long press on a phone would select the page or open its menu rather than play.
  button.addEventListener("contextmenu", (event) => event.preventDefault());
}
holdToPlay("next", () => cutTo((playing() + 1) % SCENES.length), (times) => viewer?.speed(times || 1));
holdToPlay("previous", () => cutTo((playing() + SCENES.length - 1) % SCENES.length), (times) => viewer?.rewind(times));

// Counted here, not in the viewer, which waits on three.js: on slow 3G the pill sat 12 s without a number. Pages
// serves the model gzipped with the gzipped Content-Length, while a stream counts unpacked bytes, so the share is read
// against the unpacked length a GLB states in its bytes 8-11.
async function download(url, onProgress) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0, total = 0;
  for (let read; !(read = await reader.read()).done;) {
    const chunk = read.value;
    if (!chunks.length && chunk.byteLength >= 12) total = new DataView(chunk.buffer, chunk.byteOffset).getUint32(8, true);
    chunks.push(chunk);
    loaded += chunk.byteLength;
    if (total) onProgress(Math.min(1, loaded / total));
  }
  return new Blob(chunks).arrayBuffer();
}

// The published site is the esbuild bundle (tools/build/site.mjs), which drops the importmap; the unbundled tree with
// it is the dev server.
const bundled = !document.querySelector('script[type="importmap"]');

// Each module is found only once the one importing it arrives, four round trips one after another. Asked for together
// at the load event, they download beside the model, too late to slow the poster.
// The bundle has no importmap to resolve three through, and carries three in its own chunks.
function fetchAhead() {
  if (bundled) return;
  const three = import.meta.resolve("three");
  for (const [rel, href] of [
    ["modulepreload", three],
    ["modulepreload", import.meta.resolve(new URL("three.core.js", three).href)],
    ["preload", import.meta.resolve("three/addons/libs/draco/gltf/draco_decoder.wasm")],
  ]) {
    const link = Object.assign(document.createElement("link"), { rel, href });
    if (rel === "preload") Object.assign(link, { as: "fetch", crossOrigin: "anonymous", fetchPriority: "low" });
    document.head.append(link);
  }
}

// The live scene's first frames stall the page while shaders compile and textures upload (0.7 s in iOS Safari), and
// a poster fade started before them was spent unseen: it went in one frame. `then` runs once three frames in a row
// come under 50 ms, or after 3 s on a device that never gets there.
function whenSteady(then) {
  const since = performance.now();
  let last = since, steady = 0;
  requestAnimationFrame(function step(now) {
    steady = now - last < 50 ? steady + 1 : 0;
    last = now;
    if (steady >= 3 || now - since > 3000) then();
    else requestAnimationFrame(step);
  });
}

// A scene nobody has touched for a minute drops to `IDLE_FPS` frames a second, and after `SLEEP_AFTER` minutes holds
// its last frame, sparing the battery and the fan; the next move, key, scroll or return to the tab runs it at its rate
// again. It slows rather than stops at first: the diorama is there to be watched, and watching on a phone moves no
// pointer. ?ocioso=10 slows it after 10 s (and holds it after 100 s), ?ocioso=0 never; ?dev never, as its scenes and
// the scenario tests run unattended.
const IDLE_MS = params.has("dev") ? 0 : 1000 * (Number(params.get("ocioso") ?? 60) || 0);
const IDLE_FPS = 20;
const SLEEP_AFTER = 10;

function easeOffWhenIdle() {
  let activeAt = performance.now();
  let state = "awake";
  let timer = null;
  const chosen = () => (rateKnob.isConnected ? Number(knobs.elements.fps.value) : 60);
  const doze = () => {
    clearTimeout(timer);
    const due = activeAt + IDLE_MS * (state === "awake" ? 1 : SLEEP_AFTER);
    timer = setTimeout(() => {
      if (performance.now() < due) return doze();
      if (state === "awake") {
        state = "slow";
        viewer.frameRate(Math.min(IDLE_FPS, chosen()));
        return doze();
      }
      state = "asleep";
      viewer.pause(true);
      stage.classList.add("asleep");
    }, due - performance.now());
  };
  const wake = () => {
    if (document.hidden) return;
    activeAt = performance.now();
    if (state === "awake") return;
    if (state === "asleep") viewer.pause(false);
    stage.classList.remove("asleep");
    viewer.frameRate(chosen());
    state = "awake";
    doze();
  };
  for (const type of ["pointermove", "pointerdown", "wheel", "keydown", "scroll"]) {
    addEventListener(type, wake, { capture: true, passive: true });
  }
  document.addEventListener("visibilitychange", wake);
  doze();
}

function load() {
  stage.classList.remove("waiting");
  const model = download("3d/vitorenina-diorama.glb", (share) => {
    const percent = Math.round(share * 100);
    loadingPercent.textContent = `${percent}%`;
    loading.style.setProperty("--share", `${percent}%`);
  });
  // Once the model is in, never beside it: the interior is drawn after the first frame, so it must not slow it.
  const interior = model.then(() => download("3d/vitorenina-interior.glb", () => {}));
  fetchAhead();
  setTimeout(() => stage.classList.add("slow"), 3000);
  // Fetched beside the viewer, as its scenes call their customer before the first frame.
  const dev = params.has("dev") ? import("./dev.js") : null;
  import("./3d/viewer.js")
    .then(({ mount }) => mount(stage, model, {
      interior, still, shadows: Math.min(screen.width, screen.height) < 700 ? 2048 : 4096,
      quality, adapt: pinnedQuality === null,
      covers: [brand.querySelector("h1"), brand.querySelector(".since"), knobs, panel],
      oldLogo: params.has("logo") ? "3d/old-logo.glb" : null,
      signPaint: signKnob.isConnected,
      textures: textureKnob.isConnected,
      dev: params.has("dev"),
      onZoomPast: (stop, way) => {
        const next = ZOOM_TO[way][stop];
        if (next) location.hash = HASH_FOR_STOP[next];
      },
    }))
    .then(async (loaded) => {
      viewer = loaded;
      loaded.frame(frame(), { instant: true });
      if (replay.from !== null) loaded.morning().then(() => runOn(replay.cut));
      tick();
      if (logo === "antigo") loaded.oldLogo(true);
      // The knobs as they stand now: they may have been turned while the model loaded.
      const { placa, textura, fps } = knobs.elements;
      if (signKnob.isConnected && placa.value !== "estrutura") loaded.signPaint(placa.value);
      if (textureKnob.isConnected && textura.value === "originais") loaded.textures(true);
      if (rateKnob.isConnected && fps.value !== "60") loaded.frameRate(Number(fps.value));
      // A link straight to the products walks in from the start, so the way in is seen.
      loaded.place(current === "produtos" ? "inicio" : current);
      if (current === "produtos") loaded.go("produtos");
      if (loaded.internals) {
        // `tick` lets tools/video/ show the clock it sets.
        window.diorama = Object.assign(loaded.internals, { tick });
        (await dev).devPanel({ viewer: loaded, pinned, tick });
      }
      loaded.start();
      if (IDLE_MS) easeOffWhenIdle();
      whenSteady(() => {
        stage.classList.add("ready");
        placeHelpers();
      });
      // Registered once the scene is up, so a first visit loads without it, then told what the visit used, which
      // is still in the HTTP cache. Only the bundle has a worker: a cached dev server would serve yesterday's edits.
      if (bundled) navigator.serviceWorker?.register("sw.js")
        .then(() => navigator.serviceWorker.ready)
        .then(({ active }) => active.postMessage([location.href, ...performance.getEntriesByType("resource").map((entry) => entry.name)]))
        .catch(() => {});
    })
    .catch((error) => {
      // A dropped connection or a deploy landing mid-load clears on a fresh load. Only once, as a reload is not a
      // "navigate", and not offline, where it would swap the page for the browser's error. Dev keeps its errors in view.
      if (bundled && navigator.onLine && performance.getEntriesByType("navigation")[0]?.type === "navigate") {
        report("retried", error, gpu);
        location.reload();
        return;
      }
      report("failed", error, gpu);
      replay.from = null;
      tick();
      stage.classList.add("error");
      throw error;
    });
}

const gl = document.createElement("canvas").getContext("webgl2");
const canDraw = !!gl;
const debugInfo = gl?.getExtension("WEBGL_debug_renderer_info");
const gpu = gl?.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? "";
gl?.getExtension("WEBGL_lose_context")?.loseContext();
// The scene's quality, 0 to 3 lighter (3d/viewer.js QUALITY), steps down by itself while it falls behind. A phone GPU
// of the Moto G41's class (its Mali-G52 stuttered) starts on 2, so a first visit opens without stepping down.
// ?qualidade=N holds it at N, and ?dev at 0, so films and probes draw what they always have.
const pinnedQuality = params.has("qualidade") ? Math.min(3, Math.max(0, Math.trunc(Number(params.get("qualidade"))) || 0))
  : params.has("dev") ? 0 : null;
const weakGpu = /Mali-(T|G31|G51|G52)|Adreno \(TM\) ([345]\d\d|61\d)\b|PowerVR/.test(gpu);
const quality = pinnedQuality ?? (weakGpu || navigator.deviceMemory <= 2 ? 2 : 0);
const opensOn = STOP_FOR_HASH[location.hash] ?? "inicio";
if (canDraw && !navigator.connection?.saveData && !still && following.ceu && !params.has("direto") && opensOn !== "produtos"
  && status(now()).open) {
  const date = now();
  replay.from = todayAt(hours.get(atTheShop(date).weekday).opens - MORNING_LEAD, date).getTime();
}

show(opensOn);
tick();
setInterval(tick, 30000);

if (!canDraw) {
  report("no-webgl", new Error("No WebGL 2 context"));
  stage.classList.add("error");
} else if (navigator.connection?.saveData) {
  stage.classList.add("waiting");
  stage.querySelector(".load button").addEventListener("click", load, { once: true });
} else if (document.readyState === "complete") {
  load();
} else {
  addEventListener("load", load, { once: true });
}
